import { getValue, urlQueryParams } from './helpers/custom-url-search-param';
import { IEnvironment, ISentry, IDisabledFeatures, ENVS } from './types';
import { DEBUG_CONFIG_KEY, DEV_TOOLS_KEY } from './common';

const qp = urlQueryParams(window.location.search);
const testingEnvBackend = getValue(qp, 'stgTesting');

const sentryDefaultConfig: ISentry = {
  dsn: 'https://d75569f5b0704fa59ee9e2f48d5215f2@o185340.ingest.sentry.io/6172597',
  maxBreadcrumbs: 100,
  appName: 'vod-ui-smme-portal',
  tracesSampleRate: 0,
  allowUrls: [
    /vodapayapi(dev|qa|stg)?(\.)vodacom.co.za*/,
    /www(dev|qa|stg)?(\.)vodacom.co.za*/,
    /vfsprod-domain(--test|uat)?(\.)my.salesforce.com*/,
  ],
  denyUrls: [/https:\/\/pagead2.googlesyndication.com/],
};

const Environments: { [key: string]: IEnvironment } = {
  Local: {
    name: 'Local',
    env: ENVS.LOCAL,
    uiHost: 'http://localhost:3000',
    uiBase: '/',
    apiHost: testingEnvBackend
      ? 'https://vodapayapistg.vodacom.co.za'
      : 'https://vodapayapidev.vodacom.co.za',
    apiHostCloud: 'https://wwwqa.vodacom.co.za',
    apiBase: '/cloud/public-services/financial-services/v1/smme/',
    privateBaseURL: 'https://vodapayapidev.vodacom.co.za/cloud/financial-services/v1/smme',
    tealium: 'dev',
    captchaURL: 'https://www.google.com/recaptcha/api.js',
    captchaKey: '6LfwMusZAAAAAMBsansubB8RW2A8tuGwgZYvIrZK',
    chopchopName: 'Vodapay Chop-Chop',
    kwikaName: 'VodaPay Kwika',
    orderManagementApiBaseURL: 'https://wwwdev.vodacom.co.za/cloud/financial-services',
    otpApiBaseURL:
      'https://vodapayapidev.vodacom.co.za/cloud/public-services/financial-services/v1/onetimepins/',
    pipefyBaseURL: 'https://vodacom.pipefy.com/public/form/-JAWInZq',
    allowDevTools: true,
    disabledFeatures: {
      miniAppBuyOnlineJourney: true,
      captcha: true,
      optimiseFeesCall: false,
      agentOtp: false,
      cartV2: true,
      dpo: true,
      healthCheck: true,
      oldPageRedirect: false,
      fixedVariableTiers: false,
      analytics: true,
      debugtagging: true,
      fixedFeesCheck: false,
      sovDashboard: false,
      creditVetting: false,
      passport: false,
      sentry: true,
      pagination: false,
      valueAddedServices: false,
      thirdParty: false,
      retailRevamp: false,
      imageJoin: false,
      deviceActivation: false,
      tapOnPhoneComingSoon: false,
      paymentRequest: false,
      updateBankingDetails: false,
      paymentMethodCode: false,
      updateTerminalDetails: false,
      redirectLegacyUrls: false,
      bcaTopUp: false,
      idOptimisation: false,
      idOptimisationIdNumberCheck: true,
      disableDAScanner: false,
      webAppDynamics: true,
      selfieOptimisation: false,
      applicationCardFees: false,
    },
    sentry: {
      ...sentryDefaultConfig,
      tracesSampleRate: 0.01,
    },
    webAppDynamicsConfig: {
      appKey: 'EC-AAC-UFW',
      adrumExtUrlHttp: 'http://cdn.appdynamics.com',
      adrumExtUrlHttps: 'https://cdn.appdynamics.com',
      beaconUrlHttp: 'http://fra-col.eum-appdynamics.com',
      beaconUrlHttps: 'https://fra-col.eum-appdynamics.com',
      xd: { enable: true },
      spa: { spa2: true },
      resTiming: { bufSize: 200, clearResTimingOnBeaconSend: true },
      maxUrlLength: 512,
      longStackTrace: false,
      page: {
        captureTitle: true,
      },
      fetch: true,
      isZonePromise: true,
      angular: true,
    },
  },
  Dev: {
    name: 'Development',
    env: ENVS.DEV,
    uiHost: 'https://vodapaydev.vodacom.co.za',
    uiBase: '/',
    apiHost: testingEnvBackend
      ? 'https://vodapayapistg.vodacom.co.za'
      : 'https://vodapayapidev.vodacom.co.za',
    apiHostCloud: 'https://wwwqa.vodacom.co.za',
    apiBase: '/cloud/public-services/financial-services/v1/smme/',
    tealium: 'dev',
    captchaURL: 'https://www.google.com/recaptcha/api.js',
    captchaKey: '6LfwMusZAAAAAMBsansubB8RW2A8tuGwgZYvIrZK',
    chopchopName: 'Vodapay Chop-Chop',
    kwikaName: 'VodaPay Kwika',
    orderManagementApiBaseURL: 'https://wwwdev.vodacom.co.za/cloud/financial-services',
    privateBaseURL: 'https://vodapayapidev.vodacom.co.za/cloud/financial-services/v1/smme',
    otpApiBaseURL:
      'https://vodapayapidev.vodacom.co.za/cloud/public-services/financial-services/v1/onetimepins/',
    pipefyBaseURL: 'https://vodacom.pipefy.com/public/form/-JAWInZq',
    allowDevTools: true,
    disabledFeatures: {
      miniAppBuyOnlineJourney: true,
      bca: false,
      merchantRoutingForBcaAndSelfServiceAndUseBrowserRouter: false,
      healthCheck: true,
      captcha: true,
      optimiseFeesCall: false,
      cartV2: false,
      dpo: false,
      agentOtp: false,
      oldPageRedirect: false,
      fixedVariableTiers: false,
      analytics: false,
      debugtagging: true,
      fixedFeesCheck: false,
      sovDashboard: false,
      creditVetting: false,
      passport: false,
      sentry: true,
      pagination: false,
      valueAddedServices: false,
      thirdParty: false,
      retailRevamp: false,
      imageJoin: true,
      deviceActivation: false,
      tapOnPhoneComingSoon: false,
      paymentRequest: false,
      updateBankingDetails: false,
      paymentMethodCode: false,
      updateTerminalDetails: false,
      redirectLegacyUrls: false,
      bcaTopUp: false,
      idOptimisation: false,
      idOptimisationIdNumberCheck: true,
      disableDAScanner: false,
      webAppDynamics: false,
      selfieOptimisation: false,
      applicationCardFees: false,
    },
    sentry: {
      ...sentryDefaultConfig,
      tracesSampleRate: 0.05,
    },
    webAppDynamicsConfig: {
      appKey: 'EC-AAC-UFW',
      adrumExtUrlHttp: 'http://cdn.appdynamics.com',
      adrumExtUrlHttps: 'https://cdn.appdynamics.com',
      beaconUrlHttp: 'http://fra-col.eum-appdynamics.com',
      beaconUrlHttps: 'https://fra-col.eum-appdynamics.com',
      xd: { enable: true },
      spa: { spa2: true },
      resTiming: { bufSize: 200, clearResTimingOnBeaconSend: true },
      maxUrlLength: 512,
      longStackTrace: false,
      page: {
        captureTitle: true,
      },
      fetch: true,
      isZonePromise: true,
      angular: true,
    },
  },
  QA: {
    name: 'Quality Assurance',
    env: ENVS.QA,
    uiHost: 'https://vodapayqa.vodacom.co.za',
    uiBase: '/',
    apiHost: testingEnvBackend
      ? 'https://vodapayapistg.vodacom.co.za'
      : 'https://vodapayapiqa.vodacom.co.za',
    apiHostCloud: 'https://wwwqa.vodacom.co.za',
    apiBase: '/cloud/public-services/financial-services/v1/smme/',
    tealium: 'qa',
    captchaURL: 'https://www.google.com/recaptcha/api.js',
    captchaKey: '6LfwMusZAAAAAMBsansubB8RW2A8tuGwgZYvIrZK',
    chopchopName: 'Vodapay Chop-Chop',
    kwikaName: 'VodaPay Kwika',
    orderManagementApiBaseURL: 'https://wwwqa.vodacom.co.za/cloud/financial-services',
    privateBaseURL: 'https://vodapayapiqa.vodacom.co.za/cloud/financial-services/v1/smme',
    otpApiBaseURL:
      'https://vodapayapiqa.vodacom.co.za/cloud/public-services/financial-services/v1/onetimepins/',
    pipefyBaseURL: 'https://vodacom.pipefy.com/public/form/-JAWInZq',
    allowDevTools: true,
    disabledFeatures: {
      miniAppBuyOnlineJourney: true,
      bca: false,
      merchantRoutingForBcaAndSelfServiceAndUseBrowserRouter: false,
      healthCheck: true,
      captcha: true,
      optimiseFeesCall: false,
      cartV2: false,
      dpo: true,
      agentOtp: false,
      oldPageRedirect: false,
      fixedVariableTiers: false,
      analytics: false,
      debugtagging: true,
      fixedFeesCheck: true,
      sovDashboard: true,
      creditVetting: false,
      passport: false,
      sentry: false,
      pagination: false,
      valueAddedServices: false,
      thirdParty: false,
      retailRevamp: false,
      imageJoin: true,
      deviceActivation: false,
      tapOnPhoneComingSoon: false,
      paymentRequest: false,
      updateBankingDetails: false,
      paymentMethodCode: false,
      updateTerminalDetails: false,
      redirectLegacyUrls: false,
      bcaTopUp: false,
      idOptimisation: false,
      idOptimisationIdNumberCheck: false,
      disableDAScanner: false,
      webAppDynamics: false,
      selfieOptimisation: false,
      applicationCardFees: false,
    },
    sentry: {
      ...sentryDefaultConfig,
      tracesSampleRate: 0.1,
    },
    webAppDynamicsConfig: {
      appKey: 'EC-AAC-UFW',
      adrumExtUrlHttp: 'http://cdn.appdynamics.com',
      adrumExtUrlHttps: 'https://cdn.appdynamics.com',
      beaconUrlHttp: 'http://fra-col.eum-appdynamics.com',
      beaconUrlHttps: 'https://fra-col.eum-appdynamics.com',
      xd: { enable: true },
      spa: { spa2: true },
      resTiming: { bufSize: 200, clearResTimingOnBeaconSend: true },
      maxUrlLength: 512,
      longStackTrace: false,
      page: {
        captureTitle: true,
      },
      fetch: true,
      isZonePromise: true,
      angular: true,
    },
  },
  Prod: {
    name: 'Production',
    env: ENVS.PROD,
    uiHost: 'https://vodapay.vodacom.co.za',
    uiBase: '/',
    apiHost: 'https://vodapayapi.vodacom.co.za',
    apiHostCloud: 'https://www.vodacom.co.za',
    apiBase: '/cloud/public-services/financial-services/v1/smme/',
    tealium: 'prod',
    captchaURL: 'https://www.google.com/recaptcha/api.js',
    captchaKey: '6LfwMusZAAAAAMBsansubB8RW2A8tuGwgZYvIrZK',
    chopchopName: 'Vodapay Chop-Chop',
    kwikaName: 'VodaPay Kwika',
    orderManagementApiBaseURL: 'https://www.vodacom.co.za/cloud/financial-services',
    privateBaseURL: 'https://vodapayapi.vodacom.co.za/cloud/financial-services/v1/smme',
    otpApiBaseURL:
      'https://vodapayapi.vodacom.co.za/cloud/public-services/financial-services/v1/onetimepins/',
    pipefyBaseURL: 'https://vodacom.pipefy.com/public/form/-JAWInZq',
    allowDevTools: false,
    disabledFeatures: {
      miniAppBuyOnlineJourney: true,
      bca: false,
      merchantRoutingForBcaAndSelfServiceAndUseBrowserRouter: false,
      captcha: false,
      optimiseFeesCall: false,
      agentOtp: false,
      oldPageRedirect: false,
      fixedVariableTiers: false,
      cartV2: true,
      dpo: true,
      analytics: false,
      debugtagging: true,
      fixedFeesCheck: true,
      sovDashboard: true,
      creditVetting: false,
      passport: false,
      sentry: false,
      pagination: false,
      valueAddedServices: false,
      thirdParty: false,
      retailRevamp: false,
      imageJoin: true,
      deviceActivation: false,
      tapOnPhoneComingSoon: false,
      paymentRequest: false,
      updateBankingDetails: false,
      paymentMethodCode: true,
      updateTerminalDetails: false,
      redirectLegacyUrls: false,
      bcaTopUp: false,
      idOptimisation: false,
      idOptimisationIdNumberCheck: true,
      disableDAScanner: false,
      webAppDynamics: false,
      selfieOptimisation: false,
      applicationCardFees: false,
    },
    sentry: {
      ...sentryDefaultConfig,
      tracesSampleRate: 0.1,
    },
    webAppDynamicsConfig: {
      appKey: 'EC-AAC-UFA',
      adrumExtUrlHttp: 'http://cdn.appdynamics.com',
      adrumExtUrlHttps: 'https://cdn.appdynamics.com',
      beaconUrlHttp: 'http://fra-col.eum-appdynamics.com',
      beaconUrlHttps: 'https://fra-col.eum-appdynamics.com',
      xd: { enable: true },
      spa: { spa2: true },
      resTiming: { bufSize: 200, clearResTimingOnBeaconSend: true },
      maxUrlLength: 512,
      longStackTrace: false,
      page: {
        captureTitle: true,
      },
      fetch: true,
      isZonePromise: true,
      angular: true,
    },
  },
};

Environments.Staging = {
  ...Environments.Prod,
  env: ENVS.STG,
  apiHost: 'https://vodapayapistg.vodacom.co.za',
  apiHostCloud: 'https://wwwstg.vodacom.co.za',
  orderManagementApiBaseURL: 'https://wwwstg.vodacom.co.za/cloud/financial-services',
  privateBaseURL: 'https://vodapayapistg.vodacom.co.za/cloud/financial-services/v1/smme',
  otpApiBaseURL:
    'https://vodapayapistg.vodacom.co.za/cloud/public-services/financial-services/v1/onetimepins/',
  uiHost: 'https://vodapaystg.vodacom.co.za',
  allowDevTools: false,
  disabledFeatures: {
    ...Environments.Prod.disabledFeatures,
    merchantRoutingForBcaAndSelfServiceAndUseBrowserRouter: true,
    oldPageRedirect: true,
    thirdParty: false,
    updateBankingDetails: false,
    updateTerminalDetails: false,
  },
  sentry: {
    ...sentryDefaultConfig,
    tracesSampleRate: 0.01,
  },
};

const Environment = {
  current: Environments.Prod,
};

const ENV_MATCHERS: {
  [key: string]: string[];
} = {
  [ENVS.LOCAL]: ['localhost', '127.0.0.1'],
  [ENVS.DEV]: ['dev.vodacom.co.za'],
  [ENVS.QA]: ['qa.vodacom.co.za'],
  [ENVS.STG]: ['stg.vodacom.co.za', 'staging.vodacom.co.za'],
};

const isEnv = (hostUrl: string, environment: ENVS): boolean => {
  const matchers = ENV_MATCHERS[environment];

  if (!matchers) {
    throw new Error(
      `Environment: [Critical - Error initializing environment] host: ${hostUrl} environment: ${environment}`,
    );
  }

  return matchers.find((match) => hostUrl.includes(match)) !== undefined;
};

const initEnv = (debugConfig?: IDisabledFeatures) => {
  const host = window.location.host;

  if (isEnv(host, ENVS.LOCAL)) {
    Environment.current = Environments.Local;
    Environment.current.disabledFeatures = {
      ...Environment.current.disabledFeatures,
      ...debugConfig,
    };
    return ENVS.LOCAL;
  }

  if (isEnv(host, ENVS.DEV)) {
    Environment.current = Environments.Dev;
    Environment.current.disabledFeatures = {
      ...Environment.current.disabledFeatures,
      ...debugConfig,
    };
    return ENVS.DEV;
  }

  if (isEnv(host, ENVS.QA)) {
    Environment.current = Environments.QA;
    Environment.current.disabledFeatures = {
      ...Environment.current.disabledFeatures,
      ...debugConfig,
    };
    return ENVS.QA;
  }

  if (isEnv(host, ENVS.STG)) {
    Environment.current = Environments.Staging;
    return ENVS.STG;
  }

  Environment.current = Environments.Prod;
  return ENVS.PROD;
};

const reloadEnvironmentConfig = () => {
  const devTools = sessionStorage.getItem(DEV_TOOLS_KEY);
  const debugConfig = sessionStorage.getItem(DEBUG_CONFIG_KEY);
  if (!devTools || !debugConfig) return initEnv();

  if (devTools && devTools.toLowerCase() === 'true') {
    return initEnv(JSON.parse(debugConfig));
  }
  return initEnv();
};
reloadEnvironmentConfig();

export { Environment, reloadEnvironmentConfig };
